import { launchContentPack } from "@stormglass/game-content";
import { localizer } from "@stormglass/game-localization";
import type { WeaponKind } from "@stormglass/game-core";
import { Link, useNavigate } from "react-router-dom";

import { useGameHost, useGameState } from "../game/GameContext";
import { WeatherIcon } from "./WeatherIcon";

const MAJOR_COPY: Readonly<
  Record<string, { readonly name: string; readonly description: string }>
> = {
  "upgrade.lightning.chain": {
    name: "Chain Lightning",
    description: "Bolts leap into nearby threats. Clusters become conductors.",
  },
  "upgrade.lightning.additional-arc": {
    name: "Additional Arc",
    description: "Chain Lightning reaches one additional target.",
  },
  "upgrade.lightning.overcharge": {
    name: "Overcharge",
    description: "Every sixth pulse discharges with overwhelming force.",
  },
  "upgrade.lightning.static-field": {
    name: "Static Field",
    description: "Struck threats briefly electrify the air around them.",
  },
  "upgrade.lightning.execution-voltage": {
    name: "Execution Voltage",
    description: "Bolts intensify as threats close on the chamber.",
  },
};

const unlockKind = (id: string): WeaponKind =>
  id.includes("rain")
    ? "rain"
    : id.includes("wind")
      ? "wind"
      : id.includes("hail")
        ? "hail"
        : "lightning";

export function GameOverlays(): React.JSX.Element {
  const host = useGameHost();
  const { ui, snapshot } = useGameState();
  const navigate = useNavigate();
  if (ui.phase === "milestone" && ui.milestoneOffers.length > 0)
    return (
      <div className="modal-backdrop">
        <section
          className="milestone-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="milestone-title"
        >
          <p className="eyebrow">ROUND {ui.round} // EVOLUTION</p>
          <h2 id="milestone-title">{localizer.t("milestone.title")}</h2>
          <p>{localizer.t("milestone.detail")}</p>
          <div className="milestone-grid">
            {ui.milestoneOffers.map((id, index) => {
              const definition = launchContentPack.upgrades[id];
              const copy = MAJOR_COPY[id] ?? {
                name: id.split(".").at(-1)?.replaceAll("-", " ") ?? id,
                description: "A new atmospheric behavior joins this run.",
              };
              return (
                <button
                  type="button"
                  className={`milestone-card milestone-card--${String(index + 1)}`}
                  key={id}
                  onClick={() => {
                    host.command({
                      type: "choose-milestone-upgrade",
                      upgradeId: id,
                    });
                  }}
                >
                  <span className="milestone-card__weather">
                    <WeatherIcon kind={definition?.weaponKind ?? "lightning"} />
                  </span>
                  <span className="milestone-card__index">0{index + 1}</span>
                  <strong>{copy.name}</strong>
                  <p>{copy.description}</p>
                  <small>
                    {definition?.rarity.toUpperCase() ?? "ATMOSPHERIC"} · SELECT
                  </small>
                </button>
              );
            })}
          </div>
        </section>
      </div>
    );
  if (ui.pendingPermanentChoices.length > 0)
    return (
      <div className="modal-backdrop">
        <section
          className="milestone-panel unlock-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="unlock-title"
        >
          <p className="eyebrow">PERMANENT BREAKTHROUGH</p>
          <h2 id="unlock-title">Choose the next atmosphere</h2>
          <p>
            This opens the system for research between runs. Your selection is
            permanent.
          </p>
          <div className="milestone-grid">
            {ui.pendingPermanentChoices.map((id) => {
              const kind = unlockKind(id);
              return (
                <button
                  type="button"
                  className={`milestone-card weather-choice weather-choice--${kind}`}
                  key={id}
                  onClick={() => {
                    host.command({ type: "choose-permanent-unlock", id });
                  }}
                >
                  <WeatherIcon kind={kind} />
                  <strong>{localizer.t(`weather.${kind}`)}</strong>
                  <p>
                    {kind === "rain"
                      ? "Saturate groups and conduct reactions."
                      : kind === "wind"
                        ? "Control space with powerful pressure."
                        : "Crush durable threats and apply Chill."}
                  </p>
                  <small>OPEN RESEARCH</small>
                </button>
              );
            })}
          </div>
        </section>
      </div>
    );
  if (ui.paused)
    return (
      <div className="modal-backdrop modal-backdrop--soft">
        <section
          className="pause-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pause-title"
        >
          <p className="eyebrow">SIMULATION HOLD</p>
          <h2 id="pause-title">{localizer.t("game.paused")}</h2>
          <p>{localizer.t("game.pausedDetail")}</p>
          <div className="pause-stats">
            <span>
              <small>ROUND</small>
              <strong>{ui.round}</strong>
            </span>
            <span>
              <small>INTEGRITY</small>
              <strong>{Math.ceil(ui.integrity)}</strong>
            </span>
            <span>
              <small>THREATS</small>
              <strong>{snapshot.enemies.length}</strong>
            </span>
          </div>
          <button
            className="button button--primary"
            type="button"
            onClick={() => {
              host.command({ type: "resume-run" });
            }}
          >
            Resume experiment
          </button>
          <div className="pause-links">
            <Link to="/settings">Settings</Link>
            <button
              type="button"
              onClick={() => {
                host.command({ type: "abandon-run" });
                void navigate("/results/latest");
              }}
            >
              End run
            </button>
          </div>
        </section>
      </div>
    );
  return <></>;
}
